import PropTypes from 'prop-types';
import { useState } from 'react';
import { Button, Form, Modal } from 'semantic-ui-react';
import { DescriptorProps } from '../../../../../../../constnats/descriptor';
import { DataCalcType } from '../../../../../../../models/descriptorModel';
import Alert from '../../../../../../../utils/alert';
import CloneUtils from '../../../../../../../utils/cloneUtils';
import DescriptorType from '../../../../../../../utils/descriptor/descriptorType';
import { createForm } from '../../../../../../../utils/htmlFieldsFactory';
import { NAMES_TO_SCALES_MAP } from '../../../../../../../utils/scaleUtil';
import { getAvailableBridgeEndDescriptors, getOutgoingBridgesForEditing } from '../../../../../../../utils/descriptor/outgoingBridges';

function getDefaultDescriptor(type, descriptor) {
    const copy = CloneUtils.deepClone(descriptor);
    copy[DescriptorProps.TYPE] = type
    if (type !== DescriptorType.NUMBER) {
        copy.dataCalcType = DataCalcType.NA
        copy.inputScale = NAMES_TO_SCALES_MAP.Actual
        copy.viewScale = NAMES_TO_SCALES_MAP.Actual
        copy.scalable = false
    }

    return copy
}

// descriptors is the schema's existing descriptor list used by the other form fields.
// descriptorCollection supplies the ordered first-level list for bridge section lookup.
// For a new descriptor, it includes that unsaved descriptor at its intended insertion position.
// For nested descriptors, it is empty because bridge editing currently supports only the first level.
function FieldModal({schemaId, descriptor, onSave, flatDescriptors, open, onCancel, descriptors, formSpec, descriptorCollection = [], className="" }) {

    const [descriptorInternal, setDescriptor] = useState(() => {
        const copy = CloneUtils.deepClone(descriptor);
        if (descriptorCollection.length > 0 && copy.outgoingBridges) {
            copy.outgoingBridges = getOutgoingBridgesForEditing(copy.outgoingBridges);
        }
        return copy;
    })
    const fields = descriptorInternal[DescriptorProps.TYPE] ? formSpec :
        { fields: formSpec.fields.filter(f => f.prop === DescriptorProps.TYPE) }

    function handleFieldChange(fileldName, value) {
        if (!descriptorInternal[DescriptorProps.TYPE] && fileldName === DescriptorProps.TYPE) {
            setDescriptor(prev => getDefaultDescriptor(value, prev))
        } else {
            setDescriptor((prev) => {
                const copy = CloneUtils.deepClone(prev)
                copy[fileldName] = value
                return copy
            })
        }
    }
    const usedStandardizedLabels = flatDescriptors
        .filter((d) => d[DescriptorProps.ID] !== descriptor[DescriptorProps.ID])
        .map((d) => d[DescriptorProps.STANDARDIZED_LABEL_RELATION]?.standardizedLabelId)
        .filter(x => x);

    const formContext = {
        schemaId,
        descriptor: descriptorInternal,
        flatDescriptors,
        baseHandleFieldChange: handleFieldChange,
        descriptors,
        usedStandardizedLabels,
        outgoingBridges: descriptorCollection.length > 0 ? descriptorInternal.outgoingBridges : undefined,
        availableBridgeEndDescriptors: getAvailableBridgeEndDescriptors(descriptorCollection, descriptorInternal),
    };

    const { elements, validate } = createForm(fields, descriptorInternal,
        handleFieldChange, formContext)

    function save() {
        if (validate(formContext)) {
            onSave(descriptorInternal)
        } else {
            Alert.warn("The form contains invalid data!")
        }
    }

    return <>
        <Modal open={open} className={className}>
            <Modal.Content>
                <Form className="descriptorForm">
                    {elements}
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button size='mini' positive onClick={save}>
                    Apply Changes
                </Button>
                <Button size='mini' negative onClick={onCancel}>
                    Cancel
                </Button>
            </Modal.Actions>
        </Modal>
    </>
}


FieldModal.propTypes = {

    /**
     * All schema descriptors in flat format.
     */
    flatDescriptors: PropTypes.array.isRequired,

    /**
     * Func to be called if an edit cancelation is requested.
     */
    onCancel: PropTypes.func.isRequired,

    /**
     * Func to be called if the edited data needs to be saved.
     */
    onSave: PropTypes.func.isRequired,

    /**
     * Specified if the modal is open
     */
    open: PropTypes.bool.isRequired,

    /**
     * Descriptor of the field that needs to be edited. 
     */
    descriptor: PropTypes.object.isRequired
}


export default FieldModal
